import * as React from 'react'

import * as Sentry from '@sentry/react'

import type { ErrorFallbackProps } from './types'

type BoundaryProps = React.PropsWithChildren<{
  fallbackComponent?:
    | typeof React.Component
    | React.FunctionComponent<ErrorFallbackProps>
  defaultFallbackComponent?:
    | typeof React.Component
    | React.FunctionComponent<ErrorFallbackProps>
  onError?: (
    error: Error,
    info: React.ErrorInfo,
  ) => Record<string, unknown> | void
}>

type BoundaryState =
  | { hasError: false; error?: undefined; info?: undefined }
  | { hasError: true; error: Error; info: React.ErrorInfo }

class SimpleErrorBoundary extends React.Component<
  BoundaryProps,
  BoundaryState
> {
  state: BoundaryState = { hasError: false }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    this.setState({
      hasError: true,
      error,
      info,
    })

    let extras: Record<string, unknown> | void
    try {
      extras = this.props.onError?.(error, info)
    } catch (callbackError) {
      Sentry.captureException(callbackError)
    }

    // Capture error in Sentry with React component stack
    Sentry.withScope((scope) => {
      scope.setTag('source', 'react-boundary')
      scope.setExtra('componentStack', info.componentStack)
      if (extras) scope.setExtras(extras)
      Sentry.captureException(error)
    })
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      const FallbackComponent =
        this.props.fallbackComponent || this.props.defaultFallbackComponent

      if (FallbackComponent) {
        return <FallbackComponent {...this.state} />
      }

      return (
        <h1>
          Something has gone wrong. Our team has been informed of the error.
        </h1>
      )
    }

    return this.props.children ?? null
  }
}

export default SimpleErrorBoundary
